/**
 * Client HTTP central pour l'API SkillMap.
 *
 * Lot 1 : JWT sessionStorage.
 * Lot 2 : user / domains / erp / config APIs.
 */

import i18n from "../i18n";
import { naturalSortByCode, naturalSortByDomain, sortCampaignsByYearAndDomain } from "../utils/natural-sort";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4000/api/v1";

const ACCESS_TOKEN_KEY = "skillmap.accessToken";
const REFRESH_TOKEN_KEY = "skillmap.refreshToken";

export interface ApiErrorBody {
  error: string;
  code: string;
  details?: Record<string, unknown>;
  requestId?: string;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, body?: Partial<ApiErrorBody>) {
    super(formatApiErrorMessage(body) ?? `Request failed with status ${status}`);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = body?.code;
    this.details = body?.details;
  }
}

function formatApiErrorMessage(body?: Partial<ApiErrorBody>): string | undefined {
  if (!body) return undefined;
  const base = body.error;
  if (body.code === "VALIDATION_ERROR" && body.details && typeof body.details === "object") {
    const fieldErrors = (body.details as { fieldErrors?: Record<string, string[]> }).fieldErrors;
    if (fieldErrors) {
      const parts = Object.entries(fieldErrors)
        .filter(([, msgs]) => msgs?.length)
        .map(([field, msgs]) => `${field}: ${msgs.join(", ")}`);
      if (parts.length) return `${base} (${parts.join(" · ")})`;
    }
  }
  if (body.details && typeof body.details === "object") {
    const d = body.details as Record<string, unknown>;
    const extras: string[] = [];
    if (typeof d.row === "number") extras.push(`row ${d.row}`);
    if (typeof d.path === "string") extras.push(d.path);
    if (typeof d.detail === "string" && !base?.includes(d.detail)) extras.push(d.detail);
    if (extras.length && base && !extras.every((e) => base.includes(String(e)))) {
      return `${base} [${extras.join(" · ")}]`;
    }
  }
  return base;
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

async function request<T>(path: string, options: RequestInit = {}, auth = true): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };

  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (!isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (auth) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
    });
  } catch {
    throw new ApiRequestError(0, {
      error:
        i18n.t("api_network_error"),
      code: "NETWORK_ERROR",
    });
  }

  if (response.status === 401 && auth && !path.includes("/auth/")) {
    const refreshed = await tryRefreshSession();
    if (refreshed) {
      const retryHeaders = { ...headers };
      const token = getAccessToken();
      if (token) retryHeaders.Authorization = `Bearer ${token}`;
      const retry = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers: retryHeaders,
      });
      if (!retry.ok) {
        const body = (await retry.json().catch(() => undefined)) as Partial<ApiErrorBody> | undefined;
        if (retry.status === 401) {
          clearTokens();
        }
        throw new ApiRequestError(retry.status, body);
      }
      if (retry.status === 204) return undefined as T;
      return (await retry.json()) as T;
    }
    clearTokens();
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as Partial<ApiErrorBody> | undefined;
    throw new ApiRequestError(response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshSession(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const result = await request<AuthResponse>(
          "/auth/refresh",
          { method: "POST", body: JSON.stringify({ refreshToken }) },
          false
        );
        setTokens(result.accessToken, result.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

async function requestBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as Partial<ApiErrorBody> | undefined;
    throw new ApiRequestError(response.status, body);
  }
  return response.blob();
}

export type AuthUserDto = {
  id: string;
  internalId: string;
  email: string;
  fullName: string;
  role: string;
  accessProfile: "admin" | "manager" | "expert";
  accessLevel: number;
  language: string;
  domains: string[];
};

export type UserDto = {
  id: string;
  internalId: string;
  email: string;
  fullName: string;
  role: string;
  population: string;
  workforce: "INTERNAL" | "EXTERNAL";
  language: string;
  departmentId: string | null;
  departmentCode: string | null;
  countryId: string;
  countryCode: string;
  countryPlaque: string;
  active: boolean;
  domainIds: string[];
  domains: string[];
  managerId: string | null;
  managerName: string | null;
  matrixManagerIds: string[];
  matrixManagers: Array<{ id: string; fullName: string }>;
  createdAt: string;
  updatedAt: string;
};

export type DomainDto = {
  id: string;
  code: string;
  labelEn: string;
  labelFr: string;
  active: boolean;
  departmentId: string;
  departmentCode: string;
};

export type RefItemDto = {
  id: string;
  code: string;
  labelEn: string;
  labelFr: string;
  active: boolean;
  isSystem?: boolean;
  accessLevel?: number;
  sortOrder: number;
  departmentId?: string;
  departmentCode?: string;
  plaque?: string;
};

export type ApplicationDto = {
  id: string;
  label: string;
  active: boolean;
  sortOrder: number;
};

/** @deprecated use ApplicationDto */
export type ErpDto = ApplicationDto;

export type ScaleLevelDto = {
  id: string;
  level: number;
  labelEn: string;
  labelFr: string;
  activeFrom: string;
  createdAt: string;
};

export type ThresholdDto = {
  id: string;
  value: number;
  setBy: string;
  setAt: string;
};

export type NotificationSettingsDto = {
  reminderOffsetsDays: number[];
  lastReminderRunDate: string | null;
  mailMode: "smtp" | "logged";
  smtpFrom: string;
};

export type NotificationDto = {
  id: string;
  type: string;
  recipientId: string;
  recipientEmail: string | null;
  status: string | null;
  sentAt: string | null;
  payload: Record<string, unknown> | null;
  occurrenceKey: string | null;
};

export type ReminderRunResult = {
  date: string;
  offsets: number[];
  candidates: number;
  sent: number;
  skipped: number;
  failed: number;
};

type AuthResponse = {
  user: AuthUserDto;
  accessToken: string;
  refreshToken: string;
  expiresInSeconds: number;
};

export const authApi = {
  loginConfig() {
    return request<{
      authMode: "local" | "sso";
      appEnv: string;
      showDevHint: boolean;
      hint?: { identifier: string; note: string };
    }>("/auth/login-config", { method: "GET" }, false);
  },
  login(identifier: string, password: string) {
    return request<AuthResponse>(
      "/auth/login",
      { method: "POST", body: JSON.stringify({ identifier, password }) },
      false
    );
  },
  refresh(refreshToken: string) {
    return request<AuthResponse>(
      "/auth/refresh",
      { method: "POST", body: JSON.stringify({ refreshToken }) },
      false
    );
  },
  logout() {
    return request<void>("/auth/logout", { method: "POST" });
  },
  me() {
    return request<{ user: AuthUserDto }>("/auth/me");
  },
};

export const domainsApi = {
  list() {
    return request<{ domains: DomainDto[] }>("/domains").then((r) => ({
      domains: naturalSortByCode(r.domains),
    }));
  },
};

export const referentialsApi = {
  downloadTemplate(kind: "roles" | "populations" | "languages" | "domains" | "departments" | "countries") {
    return requestBlob(`/referentials/${kind}/import-template`);
  },
  list(kind: "roles" | "populations" | "languages" | "domains" | "departments" | "countries") {
    return request<{ items: RefItemDto[] }>(`/referentials/${kind}`).then((r) =>
      kind === "domains" ? { items: naturalSortByCode(r.items) } : r
    );
  },
  create(
    kind: "roles" | "populations" | "languages" | "domains" | "departments" | "countries",
    payload: {
      code: string;
      labelEn: string;
      labelFr: string;
      active?: boolean;
      accessLevel?: number;
      sortOrder?: number;
      departmentId?: string;
      plaque?: string;
    }
  ) {
    return request<{ item: RefItemDto }>(`/referentials/${kind}`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patch(
    kind: "roles" | "populations" | "languages" | "domains" | "departments" | "countries",
    id: string,
    payload: {
      labelEn?: string;
      labelFr?: string;
      active?: boolean;
      accessLevel?: number;
      sortOrder?: number;
      departmentId?: string;
      plaque?: string;
    }
  ) {
    return request<{ item: RefItemDto }>(`/referentials/${kind}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  importBulk(
    kind: "roles" | "populations" | "languages" | "domains" | "departments" | "countries",
    file: File
  ) {
    const form = new FormData();
    form.append("file", file);
    return request<ImportBulkReport>(`/referentials/${kind}/import-bulk`, {
      method: "POST",
      body: form,
    });
  },
};

export type CreateUserPayload = {
  internalId: string;
  email: string;
  fullName: string;
  role: string;
  population?: string;
  workforce?: "INTERNAL" | "EXTERNAL";
  domainIds?: string[];
  language?: string;
  countryId: string;
  departmentId?: string | null;
  managerId?: string | null;
  matrixManagerIds?: string[];
  password?: string;
};

export type PatchUserPayload = Partial<Omit<CreateUserPayload, "internalId" | "password">> & {
  active?: boolean;
};

export type ImportBulkReport = {
  created: number;
  updated: number;
  errors: Array<{ row: number; message: string }>;
};

export const userApi = {
  list(params?: { q?: string; role?: string; active?: boolean }) {
    const qs = new URLSearchParams();
    if (params?.q) qs.set("q", params.q);
    if (params?.role) qs.set("role", params.role);
    if (params?.active !== undefined) qs.set("active", String(params.active));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<{ users: UserDto[] }>(`/users${suffix}`);
  },
  create(payload: CreateUserPayload) {
    return request<{ user: UserDto; temporaryPassword?: string }>("/users", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patch(id: string, payload: PatchUserPayload) {
    return request<{ user: UserDto }>(`/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  resetPassword(id: string, password?: string) {
    return request<{ temporaryPassword?: string }>(`/users/${id}/reset-password`, {
      method: "POST",
      body: JSON.stringify(password ? { password } : {}),
    });
  },
  downloadTemplate() {
    return requestBlob("/users/import-template");
  },
  importBulk(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<ImportBulkReport>("/users/import-bulk", {
      method: "POST",
      body: form,
    });
  },
};

export const applicationApi = {
  downloadTemplate() {
    return requestBlob("/applications/import-template");
  },
  list() {
    return request<{ applications: ApplicationDto[] }>("/applications").then(r => ({ applications: r.applications }));
  },
  create(payload: { label: string; active?: boolean; sortOrder?: number }) {
    return request<{ application: ApplicationDto }>("/applications", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patch(id: string, payload: { label?: string; active?: boolean; sortOrder?: number }) {
    return request<{ application: ApplicationDto }>(`/applications/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  importBulk(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<ImportBulkReport>("/applications/import-bulk", {
      method: "POST",
      body: form,
    });
  },
};

/** @deprecated use applicationApi */
export const erpApi = applicationApi;


export const configApi = {
  downloadScaleTemplate() {
    return requestBlob("/config/scale/import-template");
  },
  downloadThresholdTemplate() {
    return requestBlob("/config/threshold/import-template");
  },
  listScale() {
    return request<{ scale: ScaleLevelDto[] }>("/config/scale");
  },
  createScaleLevel(payload: {
    level: number;
    labelEn: string;
    labelFr: string;
    activeFrom?: string;
  }) {
    return request<{ level: ScaleLevelDto }>("/config/scale", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patchScaleLevel(
    id: string,
    payload: { labelEn?: string; labelFr?: string; activeFrom?: string }
  ) {
    return request<{ level: ScaleLevelDto }>(`/config/scale/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  getThreshold() {
    return request<{ threshold: ThresholdDto | null }>("/config/threshold");
  },
  setThreshold(value: number) {
    return request<{ threshold: ThresholdDto }>("/config/threshold", {
      method: "POST",
      body: JSON.stringify({ value }),
    });
  },
  importScaleBulk(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<ImportBulkReport>("/config/scale/import-bulk", {
      method: "POST",
      body: form,
    });
  },
  importThresholdBulk(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<ImportBulkReport>("/config/threshold/import-bulk", {
      method: "POST",
      body: form,
    });
  },
  getNotifications() {
    return request<{ settings: NotificationSettingsDto; recent: NotificationDto[] }>(
      "/config/notifications"
    );
  },
  patchNotifications(payload: { reminderOffsetsDays: number[] }) {
    return request<{ settings: NotificationSettingsDto }>("/config/notifications", {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  runReminders() {
    return request<{ result: ReminderRunResult }>("/config/notifications/run-reminders", {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
};

export type BpmlVersionDto = {
  id: string;
  label: string;
  importedAt: string;
  importedBy: string;
  active: boolean;
  lineCount: number;
  domainCount: number;
};

export type BpmlLineDto = {
  id: string;
  versionId: string;
  domain: string;
  macroProcess: string;
  businessProcess: string;
  subProcess: string | null;
  task: string | null;
  processCategory: string;
  complexityIndex: number;
  label: string | null;
  isTransversal: boolean;
  isCritical: boolean;
  parentId: string | null;
};

export type BpmlImportResult = {
  version: BpmlVersionDto;
  imported: number;
  carriedOver: number;
  domains: string[];
};

export const bpmlApi = {
  downloadTemplate() {
    return requestBlob("/bpml/import-template");
  },
  downloadEnrichTemplate() {
    return requestBlob("/bpml/enrich-template");
  },
  import(file: File, label: string) {
    const form = new FormData();
    form.append("file", file);
    form.append("label", label);
    return request<BpmlImportResult>("/bpml/import", {
      method: "POST",
      body: form,
    });
  },
  listVersions() {
    return request<{ versions: BpmlVersionDto[] }>("/bpml/versions");
  },
  activate(id: string) {
    return request<{ version: BpmlVersionDto }>(`/bpml/versions/${id}/activate`, {
      method: "POST",
    });
  },
  listLines(versionId: string, domain?: string) {
    const qs = domain ? `?domain=${encodeURIComponent(domain)}` : "";
    return request<{ lines: BpmlLineDto[] }>(`/bpml/versions/${versionId}/lines${qs}`);
  },
  listActiveLines(domain?: string) {
    const qs = domain ? `?domain=${encodeURIComponent(domain)}` : "";
    return request<{ versionId: string; lines: BpmlLineDto[] }>(`/bpml/active/lines${qs}`);
  },
  patchLine(
    id: string,
    payload: {
      label?: string | null;
      complexityIndex?: number;
      isCritical?: boolean;
      isTransversal?: boolean;
      processCategory?: "Process" | "Data" | "Reporting" | "Integration";
    }
  ) {
    return request<{ line: BpmlLineDto }>(`/bpml/lines/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  addChild(
    versionId: string,
    payload: {
      parentId: string;
      subProcess?: string;
      task?: string;
      label?: string | null;
      complexityIndex?: number;
      isCritical?: boolean;
    }
  ) {
    return request<{ line: BpmlLineDto }>(`/bpml/versions/${versionId}/lines`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  enrichBulk(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<BpmlEnrichBulkReport>("/bpml/enrich-bulk", {
      method: "POST",
      body: form,
    });
  },
  /** Remplace TOUTES les features liées à une ligne (passer [] pour tout effacer). */
  setLineFeatures(lineId: string, featureIds: string[]) {
    return request<{ features: ApplicationFeatureDto[] }>(`/bpml/lines/${lineId}/features`, {
      method: "PUT",
      body: JSON.stringify({ featureIds }),
    });
  },
  clearEnrichment(domain: string) {
    return request<{ updated: number; domain: string }>(
      `/bpml/enrich/${encodeURIComponent(domain)}`,
      { method: "DELETE" }
    );
  },
};

export type BpmlEnrichBulkReport = {
  updated: number;
  created: number;
  featuresLinked: number;
  errors: Array<{ row: number; message: string }>;
};

export type TransversalVersionDto = {
  id: string;
  label: string;
  importedAt: string;
  importedBy: string;
  active: boolean;
  skillCount: number;
};

export type TransversalSkillDto = {
  id: string;
  versionId: string;
  macroProcess: string;
  businessProcess: string;
  subProcess: string | null;
  task: string | null;
  complexityIndex: number;
  isArchived: boolean;
  sortOrder: number;
};

export type TransversalImportResult = {
  version: TransversalVersionDto;
  imported: number;
};

export const transversalApi = {
  downloadTemplate() {
    return requestBlob("/transversal/import-template");
  },
  listVersions() {
    return request<{ versions: TransversalVersionDto[] }>("/transversal/versions");
  },
  import(file: File, label: string) {
    const form = new FormData();
    form.append("file", file);
    form.append("label", label);
    return request<TransversalImportResult>("/transversal/versions/import", {
      method: "POST",
      body: form,
    });
  },
  activate(id: string) {
    return request<{ version: TransversalVersionDto }>(`/transversal/versions/${id}/activate`, {
      method: "POST",
    });
  },
  listSkills(versionId: string, includeArchived = false) {
    const qs = includeArchived ? "?includeArchived=true" : "";
    return request<{ skills: TransversalSkillDto[] }>(
      `/transversal/versions/${versionId}/skills${qs}`
    );
  },
  createSkill(payload: {
    versionId: string;
    macroProcess: string;
    businessProcess: string;
    subProcess?: string | null;
    task?: string | null;
    complexityIndex?: number;
    sortOrder?: number;
  }) {
    return request<{ skill: TransversalSkillDto }>("/transversal/skills", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patchSkill(
    id: string,
    payload: Partial<{
      macroProcess: string;
      businessProcess: string;
      subProcess: string | null;
      task: string | null;
      complexityIndex: number;
      sortOrder: number;
      isArchived: boolean;
    }>
  ) {
    return request<{ skill: TransversalSkillDto }>(`/transversal/skills/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  archiveSkill(id: string) {
    return request<{ skill: TransversalSkillDto }>(`/transversal/skills/${id}`, {
      method: "DELETE",
    });
  },
};

export type AssessmentStatus = "draft" | "submitted" | "validated";

export type AssessmentSummaryDto = {
  id: string;
  userId: string;
  userName: string;
  userInternalId: string;
  domain: string;
  /** @deprecated use applicationId */
  erpId?: string | null;
  /** @deprecated use applicationLabel */
  erpLabel?: string | null;
  applicationId: string | null;
  applicationLabel: string | null;
  year: number;
  status: AssessmentStatus;
  submittedAt: string | null;
  validatedAt: string | null;
  validatedBy: string | null;
  bpmlVersionId: string | null;
  lineCount: number;
  scoredCount: number;
};

export type AssessmentLineDto = {
  id: string;
  bpmlLineId: string;
  score: number | null;
  comment: string | null;
  domain: string;
  macroProcess: string;
  businessProcess: string;
  subProcess: string | null;
  task: string | null;
  processCategory: string;
  complexityIndex: number;
  label: string | null;
  isCritical: boolean;
  /** Environnement applicatif (ex: NICE, BEST) — null si pas d'environnement */
  environmentId: string | null;
  environmentLabel: string | null;
  /** Features BPML liées — avec hiérarchie complète pour tooltip */
  features: AssessmentLineFeatureDto[];
};

export type AssessmentLineFeatureDto = {
  code: string;
  labelEn: string;
  labelFr: string | null;
  description: string | null;
  moduleCode: string | null;
  moduleLabel: string | null;
  subModuleCode: string | null;
  subModuleLabel: string | null;
};

export type TransversalAssessmentLineDto = {
  id: string;
  transversalSkillId: string;
  score: number | null;
  comment: string | null;
  macroProcess: string;
  businessProcess: string;
  subProcess: string | null;
  task: string | null;
  complexityIndex: number;
};

export type FeatureAssessmentLineDto = {
  id: string;
  featureId: string;
  featureCode: string;
  featureLabelEn: string;
  featureLabelFr: string | null;
  moduleCode: string | null;
  moduleLabel: string | null;
  subModuleCode: string | null;
  subModuleLabel: string | null;
  score: number | null;
  comment: string | null;
};

export type ManagerCommentDto = {
  id: string;
  bpmlLineId: string | null;
  comment: string;
  authorId: string;
  authorName: string;
  createdAt: string;
};

export type AssessmentDetailDto = AssessmentSummaryDto & {
  scaleSnapshot: Array<{ level: number; labelEn: string; labelFr: string }> | null;
  lines: AssessmentLineDto[];
  transversalLines: TransversalAssessmentLineDto[];
  featureLines: FeatureAssessmentLineDto[];
  managerComments: ManagerCommentDto[];
};

export const assessmentApi = {
  list(params?: { domain?: string; year?: number; status?: AssessmentStatus }) {
    const qs = new URLSearchParams();
    if (params?.domain) qs.set("domain", params.domain);
    if (params?.year != null) qs.set("year", String(params.year));
    if (params?.status) qs.set("status", params.status);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<{ assessments: AssessmentSummaryDto[] }>(`/assessments${suffix}`);
  },
  listErp() {
    return request<{ applications: ApplicationDto[] }>("/assessments/applications");
  },
  create(payload: { domain: string; applicationId?: string | null; year: number }) {
    return request<{ assessment: AssessmentDetailDto }>("/assessments", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  get(id: string) {
    return request<{ assessment: AssessmentDetailDto }>(`/assessments/${id}`);
  },
  syncBpml(id: string) {
    return request<{ assessment: AssessmentDetailDto }>(`/assessments/${id}/sync-bpml`, {
      method: "POST",
    });
  },
  patchLines(
    id: string,
    /** Utiliser assessment_line.id (pas bpmlLineId) — stable même avec environnements multiples */
    lines: Array<{ id: string; score: number | null; comment?: string | null }>
  ) {
    return request<{ assessment: AssessmentDetailDto }>(`/assessments/${id}/lines`, {
      method: "PATCH",
      body: JSON.stringify({ lines }),
    });
  },
  patchTransversalLines(
    id: string,
    lines: Array<{
      transversalSkillId: string;
      score: number | null;
      comment?: string | null;
    }>
  ) {
    return request<{ assessment: AssessmentDetailDto }>(
      `/assessments/${id}/transversal-lines`,
      {
        method: "PATCH",
        body: JSON.stringify({ lines }),
      }
    );
  },
  patchFeatureLines(
    id: string,
    lines: Array<{ featureId: string; score: number | null; comment?: string | null }>
  ) {
    return request<{ assessment: AssessmentDetailDto }>(
      `/assessments/${id}/feature-lines`,
      {
        method: "PATCH",
        body: JSON.stringify({ lines }),
      }
    );
  },
  submit(id: string) {
    return request<{ assessment: AssessmentDetailDto }>(`/assessments/${id}/submit`, {
      method: "POST",
    });
  },
  validate(id: string, payload?: { reason?: string }) {
    return request<{ assessment: AssessmentDetailDto }>(`/assessments/${id}/validate`, {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    });
  },
  returnToExpert(id: string, comment: string) {
    return request<{ assessment: AssessmentDetailDto }>(`/assessments/${id}/return`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    });
  },
  deleteDraft(id: string) {
    return request<void>(`/assessments/${id}`, { method: "DELETE" });
  },
};

export const analyticsApi = {
  heatmap(params: AnalyticsQuery) {
    return request<HeatmapDto>(`/analytics/heatmap?${analyticsQs(params)}`);
  },
  radar(params: AnalyticsQuery & {
    mode?: "functional" | "transversal";
    grain?: number | "business" | "sub";
    parentPath?: string;
    parent?: string;
  }) {
    const qs = analyticsQs(params);
    if (params.mode) qs.set("mode", params.mode);
    if (params.grain !== undefined) qs.set("grain", String(params.grain));
    if (params.parentPath) qs.set("parentPath", params.parentPath);
    else if (params.parent) qs.set("parent", params.parent);
    return request<RadarResponseDto>(`/analytics/radar?${qs}`);
  },
  gaps(params: AnalyticsQuery) {
    return request<GapsDto>(`/analytics/gaps?${analyticsQs(params)}`);
  },
  progression(userId: string) {
    return request<ProgressionYearDto[]>(`/analytics/progression/${userId}`);
  },
  coverage(params: AnalyticsQuery) {
    return request<CoverageDto>(`/analytics/coverage?${analyticsQs(params)}`);
  },
  trainingNeeds(params: AnalyticsQuery & { weighted?: boolean }) {
    const qs = analyticsQs(params);
    if (params.weighted) qs.set("weighted", "true");
    return request<TrainingNeedsDto>(`/analytics/training-needs?${qs}`);
  },
  distribution(params: AnalyticsQuery) {
    return request<DistributionBucketDto[]>(`/analytics/distribution?${analyticsQs(params)}`);
  },
  transversalTeam(params: AnalyticsQuery) {
    return request<TransversalTeamDto>(`/analytics/transversal-team?${analyticsQs(params)}`);
  },
  criticalWorkforce(params: AnalyticsQuery) {
    return request<CriticalWorkforceDto>(
      `/analytics/insights/critical-workforce?${analyticsQs(params)}`
    );
  },
  spofCritical(params: AnalyticsQuery) {
    return request<SpofCriticalDto>(`/analytics/insights/spof-critical?${analyticsQs(params)}`);
  },
  campaign(params: AnalyticsQuery) {
    return request<CampaignInsightDto>(`/analytics/insights/campaign?${analyticsQs(params)}`);
  },
  domainBench(params: AnalyticsQuery) {
    return request<DomainBenchDto>(`/analytics/domain-bench?${analyticsQs(params)}`);
  },
  plaque(params: AnalyticsQuery) {
    return request<PlaqueDistributionDto>(`/analytics/plaque?${analyticsQs(params)}`);
  },
  rareExpertise(params: AnalyticsQuery) {
    return request<RareExpertiseDto>(`/analytics/rare-expertise?${analyticsQs(params)}`);
  },
  featureGap(params: { year: number; applicationId: string; domain?: string }) {
    const qs = new URLSearchParams({ year: String(params.year), applicationId: params.applicationId });
    if (params.domain) qs.set("domain", params.domain);
    return request<FeatureGapResult>(`/analytics/feature-gap?${qs}`);
  },
  featureHeatmap(params: { year: number; applicationId: string; moduleCode?: string; subModuleCode?: string }) {
    const qs = new URLSearchParams({ year: String(params.year), applicationId: params.applicationId });
    if (params.moduleCode) qs.set("moduleCode", params.moduleCode);
    if (params.subModuleCode) qs.set("subModuleCode", params.subModuleCode);
    return request<FeatureHeatmapResult>(`/analytics/feature-heatmap?${qs}`);
  },
};

// ── Feature Heatmap ──────────────────────────────────────────────────────────

// ── Feature Gap Analysis ──────────────────────────────────────────────────────
export type FeatureGapRisk = "critical" | "high" | "medium" | "low";

export type FeatureGapItem = {
  featureId: string;
  featureCode: string;
  featureLabelEn: string;
  featureLabelFr: string | null;
  moduleCode: string | null;
  moduleLabel: string | null;
  subModuleCode: string | null;
  subModuleLabel: string | null;
  bpmlLinesCount: number;
  criticalLinesCount: number;
  concernedProcesses: string[];
  expertsScored: number;
  expertsAboveThreshold: number;
  teamSize: number;
  avgTeamScore: number | null;
  risk: FeatureGapRisk;
};

export type FeatureGapModule = {
  moduleCode: string | null;
  moduleLabel: string | null;
  features: FeatureGapItem[];
};

export type FeatureGapResult = {
  year: number;
  applicationId: string;
  applicationLabel: string;
  domain: string | null;
  teamSize: number;
  target: number;
  summary: { total: number; critical: number; high: number; medium: number; low: number; uncovered: number };
  modules: FeatureGapModule[];
};

export type FeatureHeatmapExpert = { id: string; name: string };

export type FeatureHeatmapFeature = {
  featureId: string;
  featureCode: string;
  featureLabelEn: string;
  featureLabelFr: string | null;
  scores: Record<string, number | null>; // userId → score | null
  avg: number | null;
  coveredCount: number;
  totalExperts: number;
};

export type FeatureHeatmapSubModule = {
  subModuleCode: string | null;
  subModuleLabel: string | null;
  features: FeatureHeatmapFeature[];
};

export type FeatureHeatmapModule = {
  moduleCode: string | null;
  moduleLabel: string | null;
  subModules: FeatureHeatmapSubModule[];
};

export type FeatureHeatmapResult = {
  year: number;
  applicationId: string;
  applicationLabel: string;
  experts: FeatureHeatmapExpert[];
  modules: FeatureHeatmapModule[];
  target: number;
};

export type CampaignStatus = "draft" | "prep" | "open" | "closed";
export type CampaignStep =
  | "BPML_READY"
  | "ENRICH"
  | "PREEVAL"
  | "EXPERT_ENTRY"
  | "SUBMIT_DEADLINE"
  | "VALIDATION"
  | "ANALYSIS";

export type CampaignPhaseStatus =
  | "not_started"
  | "in_progress"
  | "blocked"
  | "delayed"
  | "done"
  | "skipped";

export type CampaignMilestoneDto = {
  step: CampaignStep;
  startsOn: string | null;
  endsOn: string | null;
  required: boolean;
  completedAt: string | null;
  completedBy: string | null;
  notes: string | null;
  phaseStatus: CampaignPhaseStatus;
  phaseComment: string | null;
  phaseStatusAt: string | null;
  phaseStatusBy: string | null;
  overdue: boolean;
};

export type CampaignDto = {
  id: string;
  year: number;
  domain: string;
  status: CampaignStatus;
  bpmlVersionId: string | null;
  transversalVersionId: string | null;
  createdBy: string;
  createdAt: string;
  openedAt: string | null;
  closedAt: string | null;
  notes: string | null;
  managersCanEditMilestones: boolean;
  milestones: CampaignMilestoneDto[];
  submitDeadline: string | null;
};

export type YearTemplateDto = {
  year: number;
  notes: string | null;
  updatedAt: string | null;
  milestones: Array<{
    step: CampaignStep;
    startsOn: string | null;
    endsOn: string | null;
    required: boolean;
  }>;
};

export type CampaignBoardDto = {
  year: number;
  steps: CampaignStep[];
  campaigns: CampaignDto[];
  summary: {
    blocked: number;
    delayed: number;
    inProgress: number;
    overdue: number;
  };
};

export type CampaignProcessTrackingDto = {
  year: number;
  domains: Array<{
    domain: string;
    campaignStatus: CampaignStatus | null;
    inProgress: number;
    validated: number;
    total: number;
  }>;
  totals: { inProgress: number; validated: number; total: number };
};

export type CampaignTransversalTrackingDto = CampaignProcessTrackingDto;

export type CampaignFeaturesTrackingDto = {
  year: number;
  // Note : `applications` n'expose pas de `code` — la table `applications` n'a pas de
  // colonne code (seulement id/label/active/sortOrder), cf. ApplicationDto plus haut.
  applications: Array<{ id: string; label: string }>;
  domains: Array<{
    domain: string;
    totalExperts: number;
    cells: Array<{
      applicationId: string;
      completed: number;
      total: number;
      hasMissingFeatures: boolean;
    }>;
  }>;
};

export const campaignApi = {
  list(params?: { year?: number; domain?: string; status?: CampaignStatus }) {
    const qs = new URLSearchParams();
    if (params?.year != null) qs.set("year", String(params.year));
    if (params?.domain) qs.set("domain", params.domain);
    if (params?.status) qs.set("status", params.status);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<{ campaigns: CampaignDto[] }>(`/campaigns${suffix}`).then((r) => ({
      campaigns: sortCampaignsByYearAndDomain(r.campaigns),
    }));
  },
  board(year: number) {
    return request<{ board: CampaignBoardDto }>(`/campaigns/board?year=${year}`).then((r) => ({
      board: {
        ...r.board,
        campaigns: naturalSortByDomain(r.board.campaigns),
      },
    }));
  },
  processTracking(year: number) {
    return request<{ tracking: CampaignProcessTrackingDto }>(
      `/campaigns/process-tracking?year=${year}`
    );
  },
  transversalTracking(year: number) {
    return request<{ tracking: CampaignTransversalTrackingDto }>(
      `/campaigns/transversal-tracking?year=${year}`
    );
  },
  featuresTracking(year: number) {
    return request<{ tracking: CampaignFeaturesTrackingDto }>(
      `/campaigns/features-tracking?year=${year}`
    );
  },
  getTemplate(year: number) {
    return request<{ template: YearTemplateDto }>(`/campaigns/templates/${year}`);
  },
  saveTemplate(payload: {
    year: number;
    notes?: string | null;
    milestones: Array<{
      step: CampaignStep;
      startsOn?: string | null;
      endsOn?: string | null;
      required?: boolean;
    }>;
  }) {
    return request<{ template: YearTemplateDto }>(`/campaigns/templates/${payload.year}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },
  generate(payload: {
    year: number;
    domains?: string[];
    updateExistingDrafts?: boolean;
  }) {
    return request<{
      created: number;
      updated: number;
      skipped: number;
      campaigns: CampaignDto[];
    }>("/campaigns/generate", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  get(id: string) {
    return request<{ campaign: CampaignDto }>(`/campaigns/${id}`);
  },
  current(year: number, domain: string) {
    const qs = new URLSearchParams({ year: String(year), domain });
    return request<{ campaign: CampaignDto | null }>(`/campaigns/current?${qs}`);
  },
  create(payload: {
    year: number;
    domain: string;
    notes?: string | null;
    managersCanEditMilestones?: boolean;
  }) {
    return request<{ campaign: CampaignDto }>("/campaigns", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patch(
    id: string,
    payload: {
      notes?: string | null;
      managersCanEditMilestones?: boolean;
      milestones?: Array<{
        step: CampaignStep;
        startsOn?: string | null;
        endsOn?: string | null;
        required?: boolean;
        notes?: string | null;
      }>;
    }
  ) {
    return request<{ campaign: CampaignDto }>(`/campaigns/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  patchPhase(
    id: string,
    payload: {
      step: CampaignStep;
      phaseStatus: CampaignPhaseStatus;
      phaseComment?: string | null;
    }
  ) {
    return request<{ campaign: CampaignDto }>(`/campaigns/${id}/phases`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  prep(id: string) {
    return request<{ campaign: CampaignDto }>(`/campaigns/${id}/prep`, { method: "POST" });
  },
  open(id: string) {
    return request<{ campaign: CampaignDto }>(`/campaigns/${id}/open`, { method: "POST" });
  },
  close(id: string) {
    return request<{ campaign: CampaignDto }>(`/campaigns/${id}/close`, { method: "POST" });
  },
};

export type PreevalStatus = "draft" | "submitted";

export type PreevalSummaryDto = {
  id: string;
  expertUserId: string;
  expertName: string;
  expertInternalId: string;
  managerUserId: string;
  managerName: string;
  domain: string;
  /** @deprecated use applicationId */
  erpId?: string | null;
  /** @deprecated use applicationLabel */
  erpLabel?: string | null;
  applicationId: string | null;
  applicationLabel: string | null;
  year: number;
  status: PreevalStatus;
  bpmlVersionId: string | null;
  campaignId: string | null;
  lineCount: number;
  scoredCount: number;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
};

export type PreevalLineDto = {
  bpmlLineId: string;
  domain: string;
  macroProcess: string;
  businessProcess: string;
  subProcess: string | null;
  task: string | null;
  label: string | null;
  isCritical: boolean;
  score: number | null;
  comment: string | null;
  /** Score Expert (auto-éval soumise/validée) — null si pas encore disponible */
  expertScore: number | null;
  /** expert − manager ; null si l’un des deux manque */
  delta: number | null;
};

export type PreevalTvLineDto = {
  transversalSkillId: string;
  macroProcess: string;
  businessProcess: string;
  subProcess: string | null;
  task: string | null;
  score: number | null;
  comment: string | null;
  expertScore: number | null;
  delta: number | null;
};

export type PreevalDetailDto = PreevalSummaryDto & {
  lines: PreevalLineDto[];
  transversalLines: PreevalTvLineDto[];
  /** Statut auto-éval Expert liée (même expert/domaine/application/année), si existante */
  assessmentStatus: string | null;
  assessmentId: string | null;
};

export type PreevalGapRowDto = {
  expertUserId: string;
  expertName: string;
  domain: string;
  /** @deprecated use applicationLabel */
  erpLabel?: string | null;
  applicationLabel: string | null;
  year: number;
  bpmlLineId: string;
  lineLabel: string;
  managerScore: number | null;
  expertScore: number | null;
  delta: number | null;
  assessmentStatus: string | null;
};

export const preevalApi = {
  list(params?: {
    year?: number;
    domain?: string;
    expertUserId?: string;
    status?: PreevalStatus;
  }) {
    const qs = new URLSearchParams();
    if (params?.year != null) qs.set("year", String(params.year));
    if (params?.domain) qs.set("domain", params.domain);
    if (params?.expertUserId) qs.set("expertUserId", params.expertUserId);
    if (params?.status) qs.set("status", params.status);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<{ preevals: PreevalSummaryDto[] }>(`/preevals${suffix}`);
  },
  gaps(params: { year: number; domain?: string; applicationId?: string; expertUserId?: string }) {
    const qs = new URLSearchParams();
    qs.set("year", String(params.year));
    if (params.domain) qs.set("domain", params.domain);
    if (params.applicationId) qs.set("applicationId", params.applicationId);
    if (params.expertUserId) qs.set("expertUserId", params.expertUserId);
    return request<{ gaps: PreevalGapRowDto[] }>(`/preevals/gaps?${qs}`);
  },
  listExperts(domain: string) {
    return request<{
      experts: Array<{ id: string; fullName: string; internalId: string; email: string }>;
    }>(`/preevals/experts?domain=${encodeURIComponent(domain)}`);
  },
  create(payload: { expertUserId: string; domain: string; applicationId?: string | null; year: number }) {
    return request<{ preeval: PreevalDetailDto }>("/preevals", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  get(id: string) {
    return request<{ preeval: PreevalDetailDto }>(`/preevals/${id}`);
  },
  patchLines(
    id: string,
    payload: {
      lines: Array<{ bpmlLineId: string; score?: number | null; comment?: string | null }>;
      transversalLines?: Array<{
        transversalSkillId: string;
        score?: number | null;
        comment?: string | null;
      }>;
    }
  ) {
    return request<{ preeval: PreevalDetailDto }>(`/preevals/${id}/lines`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  submit(id: string) {
    return request<{ preeval: PreevalDetailDto }>(`/preevals/${id}/submit`, {
      method: "POST",
    });
  },
};

export type ExportJobStatus = "queued" | "running" | "ready" | "failed";

export type ExportJobDto = {
  id: string;
  status: ExportJobStatus;
  format: string;
  filters: Record<string, unknown>;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  expiresAt: string | null;
  downloadPath: string | null;
};

export const exportApi = {
  request(payload: {
    year: number;
    domain?: string;
    applicationId?: string;
    includeStatus?: "submitted_validated" | "validated_only";
    format?: "xlsx";
  }) {
    return request<{ job: ExportJobDto }>("/export", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  listJobs() {
    return request<{ jobs: ExportJobDto[] }>("/export/jobs");
  },
  getJob(id: string) {
    return request<{ job: ExportJobDto }>(`/export/jobs/${id}`);
  },
  downloadFile(id: string) {
    return requestBlob(`/export/jobs/${id}/file`);
  },
};

export type AuditEntryDto = {
  id: string;
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  actionType: string;
  targetType: string;
  targetId: string | null;
  reason: string | null;
  details: {
    changed?: Record<string, { from: unknown; to: unknown }>;
    summary?: Record<string, unknown>;
  } | Record<string, unknown> | null;
  createdAt: string;
};

export const auditApi = {
  list(params: {
    page?: number;
    pageSize?: number;
    actionType?: string;
    targetType?: string;
    actorId?: string;
    q?: string;
    from?: string;
    to?: string;
  }) {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.pageSize) qs.set("pageSize", String(params.pageSize));
    if (params.actionType) qs.set("actionType", params.actionType);
    if (params.targetType) qs.set("targetType", params.targetType);
    if (params.actorId) qs.set("actorId", params.actorId);
    if (params.q) qs.set("q", params.q);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<{
      items: AuditEntryDto[];
      total: number;
      page: number;
      pageSize: number;
    }>(`/audit-log${suffix}`);
  },
  actionTypes() {
    return request<{ actionTypes: string[] }>("/audit-log/action-types");
  },
};

export { request };

function analyticsQs(params: AnalyticsQuery): URLSearchParams {
  const qs = new URLSearchParams();
  qs.set("year", String(params.year));
  if (params.domain) qs.set("domain", params.domain);
  if (params.applicationId) qs.set("applicationId", params.applicationId);
  if (params.userId) qs.set("userId", params.userId);
  return qs;
}

export type AnalyticsQuery = {
  year: number;
  domain?: string;
  /** @deprecated use applicationId */
  erpId?: string;
  applicationId?: string;
  userId?: string;
};

export type AnalyticsLineMeta = {
  id: string;
  domain: string;
  macroProcess: string;
  businessProcess: string;
  subProcess: string | null;
  task: string | null;
  complexityIndex: number;
  isCritical: boolean;
};

export type HeatmapDto = {
  users: Array<{ id: string; name: string }>;
  rows: Array<{
    line: AnalyticsLineMeta;
    avg: number | null;
    scores: Array<{ userId: string; score: number | null }>;
  }>;
  threshold: number;
};

export type RadarPointDto = {
  label: string;
  macroProcess: string;
  avg: number;
  drillable?: boolean;
};

export type RadarParentOption = {
  key: string;
  label: string;
  macroProcess: string;
  nextGrain?: number;
  childCount?: number;
  subCount: number;
};

export type RadarResponseDto = {
  points: RadarPointDto[];
  grain: number | "business" | "sub";
  parentPath?: string | null;
  parent: string | null;
  parents: RadarParentOption[];
};

export type GapsDto = {
  target: number;
  gaps: Array<{
    line: AnalyticsLineMeta;
    avg: number;
    target: number;
    gap: number;
    belowCount: number;
    total: number;
  }>;
};

export type ProgressionYearDto = {
  year: number;
  byMacroProcess: Record<string, number>;
};

export type CoverageDto = {
  target: number;
  coverage: Array<{
    line: AnalyticsLineMeta;
    aboveTarget: number;
    total: number;
    isCritical: boolean;
    isSpof: boolean;
  }>;
};

export type TrainingNeedsDto = {
  needs: Array<{
    line: AnalyticsLineMeta;
    cumulativeGap: number;
    expertCount: number;
  }>;
};

export type DistributionBucketDto = { score: number; count: number };

export type TransversalTeamDto = {
  users: Array<{ id: string; name: string }>;
  skills: Array<{
    skill: {
      id: string;
      macroProcess: string;
      businessProcess: string;
      subProcess: string | null;
      task: string | null;
      complexityIndex: number;
      label?: string;
    };
    avg: number | null;
    scores: Array<{ userId: string; score: number | null }>;
  }>;
  threshold: number;
};

export type CriticalWorkforceDto = {
  target: number;
  summary: { total: number; onlyExternal: number; uncovered: number };
  lines: Array<{
    line: AnalyticsLineMeta;
    internalAbove: number;
    externalAbove: number;
    internalTotal: number;
    externalTotal: number;
    onlyExternal: boolean;
    uncovered: boolean;
  }>;
};

export type SpofCriticalDto = {
  target: number;
  summary: { uncovered: number; spof: number };
  items: Array<{
    line: AnalyticsLineMeta;
    aboveTarget: number;
    total: number;
    risk: "uncovered" | "spof";
  }>;
};

export type CampaignInsightDto = {
  year: number;
  totals: { draft: number; submitted: number; validated: number; total: number };
  domains: Array<{
    domain: string;
    draft: number;
    submitted: number;
    validated: number;
    total: number;
    validatedPct: number;
    submittedOrValidatedPct: number;
  }>;
};

export type DomainBenchDto = {
  target: number;
  domains: Array<{
    domain: string;
    expertCount: number;
    assessmentCount: number;
    scoredCells: number;
    avgScore: number;
    pctAtOrAbove: number;
  }>;
};

export type PlaqueDistributionDto = {
  target: number;
  minCohort: number;
  countriesVisible: boolean;
  plaques: Array<{
    plaque: string;
    expertCount: number;
    scoredCells: number;
    avgScore: number | null;
    pctAtOrAbove: number | null;
  }>;
  countries: Array<{
    plaque: string;
    countryCode: string;
    countryLabelEn: string;
    countryLabelFr: string;
    expertCount: number;
    scoredCells: number;
    avgScore: number;
    pctAtOrAbove: number;
  }>;
};

export type RareExpertiseDto = {
  target: number;
  maxExperts: number;
  summary: { total: number; single: number; rare: number; critical: number };
  items: Array<{
    line: AnalyticsLineMeta;
    aboveTarget: number;
    total: number;
    lineIsCritical: boolean;
    risk: "single" | "rare";
  }>;
};

// ─── Application Environments ─────────────────────────────────────────────────

export type ApplicationEnvironmentDto = {
  id: string;
  applicationId: string;
  applicationLabel: string;
  label: string;
  active: boolean;
  sortOrder: number;
};

export const applicationEnvironmentsApi = {
  list(applicationId?: string, includeInactive = false) {
    const qs = new URLSearchParams();
    if (applicationId) qs.set("applicationId", applicationId);
    if (includeInactive) qs.set("includeInactive", "true");
    const q = qs.toString();
    return request<{ environments: ApplicationEnvironmentDto[] }>(`/application-environments${q ? "?" + q : ""}`);
  },
  listByApplication(appId: string) {
    return request<{ environments: ApplicationEnvironmentDto[] }>(`/application-environments/by-application/${appId}`);
  },
  create(payload: { applicationId: string; label: string; sortOrder?: number }) {
    return request<{ environment: ApplicationEnvironmentDto }>("/application-environments", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patch(id: string, patch: { label?: string; active?: boolean; sortOrder?: number }) {
    return request<{ environment: ApplicationEnvironmentDto }>(`/application-environments/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  delete(id: string) {
    return request<void>(`/application-environments/${id}`, { method: "DELETE" });
  },
  // Environments d'une ligne BPML
  getForLine(lineId: string) {
    return request<{ environments: ApplicationEnvironmentDto[] }>(`/bpml/lines/${lineId}/environments`);
  },
  setForLine(lineId: string, environmentIds: string[]) {
    return request<{ environments: ApplicationEnvironmentDto[] }>(`/bpml/lines/${lineId}/environments`, {
      method: "PUT",
      body: JSON.stringify({ environmentIds }),
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────

export type ApplicationFeatureDto = {
  id: string;
  applicationId: string;
  applicationLabel: string;
  /** N1 */
  moduleCode: string | null;
  moduleLabel: string | null;
  /** N2 */
  subModuleCode: string | null;
  subModuleLabel: string | null;
  /** N3 — clé unique par application */
  code: string;
  labelEn: string;
  labelFr: string | null;
  description: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
};

export const applicationFeaturesApi = {
  downloadTemplate() {
    return requestBlob("/application-features/import-template");
  },
  list(params?: { applicationId?: string; activeOnly?: boolean }) {
    const qs = new URLSearchParams();
    if (params?.applicationId) qs.set("applicationId", params.applicationId);
    if (params?.activeOnly) qs.set("activeOnly", "true");
    const q = qs.toString();
    return request<{ features: ApplicationFeatureDto[] }>(`/application-features${q ? "?" + q : ""}`);
  },
  create(payload: {
    applicationId: string;
    code: string;
    labelEn: string;
    labelFr?: string;
    description?: string;
    moduleCode?: string;
    moduleLabel?: string;
    subModuleCode?: string;
    subModuleLabel?: string;
    active?: boolean;
    sortOrder?: number;
  }) {
    return request<{ feature: ApplicationFeatureDto }>("/application-features", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  patch(id: string, payload: {
    labelEn?: string; labelFr?: string; description?: string;
    moduleCode?: string; moduleLabel?: string;
    subModuleCode?: string; subModuleLabel?: string;
    active?: boolean; sortOrder?: number;
  }) {
    return request<{ feature: ApplicationFeatureDto }>(`/application-features/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  importBulk(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<{ created: number; updated: number; errors: Array<{ row: number; message: string }> }>(
      "/application-features/import-bulk",
      { method: "POST", body: form }
    );
  },
  getByLine(bpmlLineId: string, applicationId?: string) {
    const qs = new URLSearchParams();
    if (applicationId) qs.set("applicationId", applicationId);
    const q = qs.toString();
    return request<{ features: ApplicationFeatureDto[] }>(`/application-features/by-line/${bpmlLineId}${q ? "?" + q : ""}`);
  },
};

// ─── Feature Scores (My Application Skills) ───────────────────────────────────

export type FeatureScoreDto = {
  featureId: string;
  featureCode: string;
  featureLabelEn: string;
  featureLabelFr: string | null;
  moduleCode: string | null;
  moduleLabel: string | null;
  subModuleCode: string | null;
  subModuleLabel: string | null;
  applicationId: string;
  applicationLabel: string;
  score: number | null;
  comment: string | null;
  updatedAt: string | null;
};

export const featureScoresApi = {
  get(year: number, applicationId?: string) {
    const qs = new URLSearchParams({ year: String(year) });
    if (applicationId) qs.set("applicationId", applicationId);
    return request<{ scores: FeatureScoreDto[] }>(`/feature-scores?${qs.toString()}`);
  },
  patch(year: number, lines: Array<{ featureId: string; score: number | null; comment?: string | null }>) {
    return request<{ scores: FeatureScoreDto[] }>("/feature-scores", {
      method: "PATCH",
      body: JSON.stringify({ year, lines }),
    });
  },
  /** Lot 3 — « prérenseigner » : ne remplit que les features actives sans score existant. */
  bulkApply(payload: { applicationId: string; year: number; score: number; comment?: string | null }) {
    return request<{ appliedCount: number }>("/feature-scores/bulk-apply", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

export type TransversalScoreDto = {
  transversalSkillId: string;
  macroProcess: string;
  businessProcess: string;
  subProcess: string | null;
  task: string | null;
  complexityIndex: number;
  score: number | null;
  comment: string | null;
  updatedAt: string | null;
};

export type TransversalSessionDto = {
  id: string;
  year: number;
  status: AssessmentStatus;
  submittedAt: string | null;
  validatedAt: string | null;
};

export type TransversalScoresBundleDto = {
  year: number;
  scores: TransversalScoreDto[];
  session: TransversalSessionDto;
};

export type TransversalAssessmentSummaryDto = {
  id: string;
  userId: string;
  userName: string;
  userInternalId: string;
  year: number;
  status: AssessmentStatus;
  submittedAt: string | null;
  validatedAt: string | null;
  validatedBy: string | null;
  lineCount: number;
  scoredCount: number;
};

export type TransversalAssessmentDetailDto = TransversalAssessmentSummaryDto & {
  scaleSnapshot: ScaleLevelDto[] | null;
  scores: TransversalScoreDto[];
};

export const transversalScoresApi = {
  get(year: number) {
    return request<TransversalScoresBundleDto>(`/transversal-assessments/scores?year=${year}`);
  },
  patch(
    year: number,
    lines: Array<{ transversalSkillId: string; score: number | null; comment?: string | null }>
  ) {
    return request<void>("/transversal-assessments/scores", {
      method: "PATCH",
      body: JSON.stringify({ year, lines }),
    });
  },
};

export const transversalAssessmentApi = {
  list(params?: { year?: number; status?: AssessmentStatus }) {
    const qs = new URLSearchParams();
    if (params?.year != null) qs.set("year", String(params.year));
    if (params?.status) qs.set("status", params.status);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<{ assessments: TransversalAssessmentSummaryDto[] }>(
      `/transversal-assessments${suffix}`
    );
  },
  get(id: string) {
    return request<{ assessment: TransversalAssessmentDetailDto }>(`/transversal-assessments/${id}`);
  },
  submit(id: string) {
    return request<{ assessment: TransversalAssessmentDetailDto }>(
      `/transversal-assessments/${id}/submit`,
      { method: "POST" }
    );
  },
  validate(id: string, payload?: { reason?: string }) {
    return request<{ assessment: TransversalAssessmentDetailDto }>(
      `/transversal-assessments/${id}/validate`,
      { method: "POST", body: JSON.stringify(payload ?? {}) }
    );
  },
  return(id: string, comment: string) {
    return request<{ assessment: TransversalAssessmentDetailDto }>(
      `/transversal-assessments/${id}/return`,
      { method: "POST", body: JSON.stringify({ comment }) }
    );
  },
};
